import { MoreHorizontal, Plus } from "lucide-react"
import { ImageIcon } from "./assets"
import { DeleteConfirmModal, Tooltip } from "../ui/modal"
import { IconButton } from "../common/icon-button"
import { useTranslation } from "react-i18next"
import { useState } from "react"
import { cn } from "@/lib/utils"
import { MoreActionItem, MoreActionPopover } from "../common/more-action-popover"
import { RenameIcon, TrashIcon } from "@/components/ui/icon";
import { RenameModal } from "../common/rename-modal"
import { ModalTagCreate } from "./modal-tag-create"
import { MaterialTagData } from "@/services/material-tag-list"
import { ConfirmModal } from "../common/confirm-modal"
import { MaterialItemData } from "@/services/user-material-get-all-folder-items"
import { ModalTagChoose } from "./modal-tag-choose"

interface TagSectionProps {
  tags: MaterialTagData[];
  materialItems?: MaterialItemData[];
  activeTagId?: number;
  createLoading?: boolean;
  deleteLoading?: boolean;
  onSelectTag?: (id: number) => void;
  onCreate?: (name: string) => Promise<void>;
  onDelete?: (ids: number[]) => Promise<void>;
  onRemoveAndDelete?: (params: { tagId: number, newTagIds: number[], moveItemIds: number[] }) => Promise<void>;
  onRename?: (params: { id: number, name: string }) => Promise<void>;
}

/** 素材标签区域 */
export const TagSection = ({
  tags,
  materialItems,
  createLoading,
  deleteLoading,
  activeTagId,
  onSelectTag,
  onCreate,
  onDelete,
  onRemoveAndDelete,
  onRename
}: TagSectionProps) => {
  const { t } = useTranslation();
  // 记录当前哪个 Tag 的菜单是打开的
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSelectTagModal, setShowSelectTagModal] = useState(false);
  const [showTagChooseModal, setShowTagChooseModal] = useState(false);

  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [removeAndDeleteLoading, setRemoveAndDeleteLoading] = useState(false);
  const [useItems, setUseItems] = useState<MaterialItemData[]>([]);
  const [useCount, setUseCount] = useState(0);

  const handleCreate = async (name: string) => {
    await onCreate?.(name);
  }

  const handleRename = async (id: number, name: string) => {
    onRename?.({ id, name })
  }

  // 确认删除
  const confirmDelete = async () => {
    if (!deleteId) return;
    await onDelete?.([deleteId!]);
  }

  // 移动+删除
  const removeAndDelete = async (ids: number[]) => {
    try {
      setRemoveAndDeleteLoading(true);
      await onRemoveAndDelete?.({
        tagId: deleteId!,
        newTagIds: ids,
        moveItemIds: useItems.map(item => item.id),
      });
    } catch (error) {
      console.error("Failed to remove and delete assets material item tags:", error);
      throw error;
    } finally {
      setRemoveAndDeleteLoading(false);
    }
  }

  // 判断删除逻辑
  const handleDelete = async (id: number) => {
    setDeleteId(id);

    // 过滤出属于当前标签的素材
    const itemsWithCurrentTag = materialItems?.filter(item => item.tags.map(tag => tag.id).includes(id));

    // 过滤出 itemsWithCurrentTag 素材中,标签等于1的素材
    const itemsWithCurrentTagOnly = itemsWithCurrentTag?.filter(item => item.tags.length === 1);

    const count = itemsWithCurrentTagOnly?.length

    setUseCount(count)
    setUseItems(itemsWithCurrentTagOnly || [])

    if (count > 0) {
      // 展示标签已被应用弹窗
      setShowSelectTagModal(true);
    } else {
      // 二次确认弹窗
      setShowConfirmModal(true);
    }
  }

  const handleTagClick = (id: number) => {
    onSelectTag?.(id);
  }

  return (
    <div className={cn("mb-3 px-8 select-none z-20")}>
      <div className="w-full max-h-max flex flex-wrap items-center gap-[10px]">
        <div
          className={cn(
            "max-w-max min-w-max flex gap-1.5 py-1.5 px-[10px] rounded-[6px] cursor-pointer mr-2 transition-all hover:bg-[rgba(16,24,40,0.05)] flex-shrink-0",
            activeTagId === 0 && "bg-[rgba(16,24,40,0.05)]"
          )}
          onClick={() => handleTagClick(0)}
        >
          <ImageIcon className={cn(
            "size-5 text-[rgba(71,84,103,1)] transition-all",
            activeTagId === 0 && "text-[rgba(16,24,40,1)]"
          )} />
          <span className="text-sm text-[rgba(16,24,40,1)] font-medium">{t('assets.material_item.tags.all_image')}</span>
        </div>
        <CreateTagButton onCreate={handleCreate} loading={createLoading} />

        {tags.map(item => {
          // 判断当前 Tag 菜单是否打开
          const isMenuOpen = openMenuId === item.id;
          const isActive = activeTagId === item.id;
          return (
            <div
              key={item.id}
              className={cn(
                "group flex items-center max-w-max max-h-8 py-1.5 px-[10px] rounded-[6px] cursor-pointer transition-all",
                isActive ? "bg-[rgba(16,24,40,0.05)]" : "hover:bg-[rgba(16,24,40,0.05)]"
              )}
              onClick={() => handleTagClick(item.id)}
            >
              <span
                className={cn(
                  "text-sm text-[rgba(71,84,103,1)] font-medium transition-all whitespace-nowrap",
                  isActive ? "text-[rgba(16,24,40,1)]" : "group-hover:text-[rgba(16,24,40,1)]"
                )}
              >
                {item.tagName}
              </span>

              {/* 更多操作 */}
              <div
                className={cn(
                  "flex items-center overflow-hidden transition-all duration-300 ease-in-out",
                  (isMenuOpen)
                    ? "w-6 opacity-100 ml-2"
                    : "w-0 opacity-0 ml-0 group-hover:w-6 group-hover:opacity-100 group-hover:ml-2"
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreActionPopover
                  className="w-40"
                  side="bottom"
                  onOpenChange={(open) => setOpenMenuId(open ? item.id : null)}
                  tiggerIcon={
                    <MoreHorizontal className="size-5 text-black" />
                  }
                  tiggerClassName={cn(
                    "hover:bg-[rgba(16,24,40,0.1)] bg-transparent",
                  )}
                >
                  <RenameModal
                    currentName={item.tagName}
                    trigger={
                      <MoreActionItem
                        icon={<RenameIcon className="w-4 h-4" />}
                        label={t("common.rename")}
                      />
                    }
                    onConfirm={(name) => handleRename(item.id, name)}
                  />
                  <MoreActionItem
                    icon={<TrashIcon className="w-4 h-4" />}
                    label={t("common.delete")}
                    onClick={() => handleDelete(item.id)}
                  />
                </MoreActionPopover>
              </div>
            </div>
          );
        })}
      </div>
      {/* 二次确认删除弹窗 */}
      {showConfirmModal && (
        <DeleteConfirmModal
          open={showConfirmModal}
          onOpenChange={setShowConfirmModal}
          loading={deleteLoading}
          onConfirm={confirmDelete}
        />
      )}
      {/* 标签已被使用确认弹窗  */}
      {showSelectTagModal && (
        <ConfirmModal
          open={showSelectTagModal}
          title={t('assets.material_item.tags.delete_title')}
          description={t('assets.material_item.tags.delete_desc', { count: useCount })}
          cancel={t('common.cancel')}
          confirm={t('assets.material_item.tags.replace')}
          onConfirm={() => setShowTagChooseModal(true)}
          onOpenChange={setShowSelectTagModal}
        />
      )}
      {showTagChooseModal && (
        <ModalTagChoose
          open={showTagChooseModal}
          tags={tags}
          title={t('assets.material_item.tag_choose_modal.replace')}
          files={useItems.map(item => ({
            type: item.itemType as 'image' | 'video',
            url: item.itemUrl,
          }))}
          onOpenChange={setShowTagChooseModal}
          onCreate={onCreate}
          createLoading={createLoading}
          onConfirm={removeAndDelete}
          loading={removeAndDeleteLoading}
          disabledIds={[deleteId]}
        />
      )}
    </div>
  )
}

/** 新建标签按钮 */
const CreateTagButton = ({
  onCreate,
  loading
}: {
  loading?: boolean
  onCreate?: (name: string) => Promise<void>
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleCreate = async (name: string) => {
    if (onCreate) {
      await onCreate(name);
      setOpen(false);
    }
  }

  return (
    <div className="inline-block max-w-max max-h-max min-w-max">
      <Tooltip
        side="bottom"
        align="center"
        trigger={
          <IconButton
            className="size-8  hover:bg-[rgba(16,24,40,0.05)]"
            icon={<Plus className="size-5" />}
            onClick={() => setOpen(true)}
          />
        }
        content={
          <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
            <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
              {t('assets.material_item.tags.create')}
            </span>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-black"></div>
          </div>
        }
      />
      {open && (
        <ModalTagCreate
          open={open}
          onOpenChange={setOpen}
          onConfirm={handleCreate}
          loading={loading}
        />
      )}
    </div>
  )
}